/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react'
import { get, values, map, head, isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import cn from 'classnames'
import generateUuid from 'uuid/v4'

import SidebarModal from '../SidebarModal'
import CardReader from '../CardReader'
import TabOrderTotals from './TabOrderTotals'
import Button from '../Button'

import styles from './StartTabModal.module.scss'
import { centsToDollars } from '../../utils/formatters'
import VNNotesField from '../VNNotesField'
import { nextPosOrderNumber } from '../../utils/orderNumbers'

const StartTabModal = ({
  className,
  clearRemoteOrderTotal,
  exemptions,
  itemCount,
  noOpPayments,
  onClose,
  onDone,
  onMount,
  onRemotePayment,
  onSelectNoOpPayment,
  onSuccess,
  paymentOptions,
  peripheralStatus,
  remoteOrderTotal,
  show,
  subtotal,
  title,
  total,
  totalDisplayWithExemptions,
  ...props
}) => {
  const classNames = cn(styles.startTabModal, className)
  const [uuid, setUuid] = useState(generateUuid())
  const [cardPaymentCompleted, setCardPaymentCompleted] = useState(false)
  const [showCardReader, setShowCardReader] = useState(false)
  const [showSavedPayments, setShowSavedPayments] = useState(false)
  const totalIsFetched = !remoteOrderTotal.loading && !!remoteOrderTotal.totalDisplay
  const formattedTotal = totalDisplayWithExemptions.replace('$', '')
  const notesRef = useRef()

  const handleOnSuccess = (data) => {
    onSuccess(data, notesRef.current?.getNotes())
    setCardPaymentCompleted(true)
  }

  const makePaymentButton = (paymentPreference, index, collection) => {
    const classNames =
      collection?.length > 1
        ? styles.actionButton
        : [styles.actionButton, styles.mintGreenHighlight]

    return (
      <Button
        disabled={!totalIsFetched}
        key={paymentPreference?.externalRefId}
        onClick={() => onRemotePayment(exemptions, paymentPreference, notesRef.current?.getNotes())}
        className={classNames}
      >
        {paymentPreference?.shortDescription}
      </Button>
    )
  }

  useEffect(() => {
    setUuid(remoteOrderTotal.uuid)
  }, [totalIsFetched])

  useEffect(() => {
    onMount()
  }, [])
  let footer
  if (showCardReader) {
    footer = (<CardReader // FIXME: There is a memory leak here when you close the modal. Solution unclear.
    uuid={uuid}
    total={formattedTotal}
    onCancel={onClose}
    onSuccess={handleOnSuccess}
    onDone={onDone}
    authorizationType="auth"
    exemptions={exemptions}
    isNewTab={true}
    peripheralStatus={peripheralStatus}
    orderNumber={nextPosOrderNumber(true)}
  />)
  } else if (showSavedPayments) {
    footer = map(paymentOptions, makePaymentButton)
  } else {

    let paymentButton = null
    if (paymentOptions?.length > 1) {
      paymentButton = <Button disabled={!totalIsFetched} key='saved payments' onClick={() => setShowSavedPayments(true)} className={styles.actionButton} >Saved Payments</Button>
    } else if (!isEmpty(paymentOptions)) {
      paymentButton = makePaymentButton(head(paymentOptions))
    }

    footer = [
      <Button disabled={!totalIsFetched} key='show card reader' onClick={() => setShowCardReader(true)} className={styles.actionButton} >Card Reader</Button>,
      !isEmpty(noOpPayments) && <Button disabled={!totalIsFetched} key='no-op' onClick={() => onSelectNoOpPayment(exemptions, notesRef.current?.getNotes())} className={styles.actionButton}>Other</Button>,
      paymentButton,
    ]
  }

  let serviceChargeDisplay = remoteOrderTotal?.serviceChargeDisplay ?? '-'
  serviceChargeDisplay = remoteOrderTotal?.serviceChargeInCents + Math.abs(remoteOrderTotal?.serviceChargeDiscountInCents)
  serviceChargeDisplay = centsToDollars(serviceChargeDisplay)
  let discountDisplay = remoteOrderTotal?.discountDisplay ?? '-'

  if (!exemptions.applyServiceFee) {
    serviceChargeDisplay = undefined

    if (remoteOrderTotal?.serviceChargeDiscountInCents) {
      const calculatedDiscount = remoteOrderTotal?.discountAmountInCents - remoteOrderTotal?.serviceChargeDiscountInCents
      discountDisplay = centsToDollars(calculatedDiscount)
    }
  }

  const handleOnClose = () => {
    if (cardPaymentCompleted) {
      onDone()
    } else {
      onClose()
    }
    clearRemoteOrderTotal()
  }

  return (
    <div>
      <SidebarModal
        title={title}
        show={show}
        className={classNames}
        onClose={handleOnClose}
        {...props}
      >
        <div className={styles.bodyContainer}>
          <TabOrderTotals
            className={styles.tabOrderTotals}
            discount={discountDisplay}
            exemptions={exemptions}
            itemCount={itemCount}
            nonExemptableTaxRules={values(get(remoteOrderTotal, 'nonExemptableTaxRules') || [])}
            serviceCharge={serviceChargeDisplay}
            serviceChargeName={get(remoteOrderTotal, 'serviceChargeName')}
            subtotal={subtotal}
            tax={get(remoteOrderTotal, 'taxDisplay') || '-'}
            total={totalDisplayWithExemptions}
          />
          <VNNotesField ref={notesRef}/>
          <div className={styles.buttonContainer}>
            {footer}
          </div>
        </div>
      </SidebarModal>
    </div>
  )
}

StartTabModal.defaultProps = {
  itemCount: 0,
  onClose: () => {},
  onDone: () => {},
  onSuccess: () => {},
  remoteOrderTotal: {},
  show: false,
  subtotal: '',
  title: '',
  total: '',
  totalDisplayWithExemptions: '',
}

StartTabModal.propTypes = {
  itemCount: PropTypes.number,
  onClose: PropTypes.func,
  onDone: PropTypes.func,
  onSuccess: PropTypes.func,
  remoteOrderTotal: PropTypes.shape({
    discountDisplay: PropTypes.string,
    serviceChargeDisplay: PropTypes.string,
    taxDisplay: PropTypes.string,
    totalDisplay: PropTypes.string,
  }),
  show: PropTypes.bool,
  subtotal: PropTypes.string,
  title: PropTypes.string,
  total: PropTypes.string,
  totalDisplayWithExemptions: PropTypes.string,
}

export default StartTabModal
